import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Copy, Star, Clock, Tag, MessageSquare } from "lucide-react";
import { formatDistanceToNow } from 'date-fns';

interface Prompt {
  id: string;
  raw_prompt: string;
  refined_prompt: string;
  category: string;
  created_at: string;
  score?: number;
  feedback?: string[];
}

interface PromptHistoryModalProps {
  prompt: Prompt | null;
  isOpen: boolean;
  onClose: () => void;
  onCopy: (text: string) => void;
}

const PromptHistoryModal = ({ prompt, isOpen, onClose, onCopy }: PromptHistoryModalProps) => {
  const getCategoryColor = (category: string) => {
    switch (category?.toLowerCase()) {
      case 'image':
        return 'bg-pink-500';
      case 'video':
        return 'bg-purple-500';
      case 'app':
        return 'bg-blue-500';
      case 'text':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const handleCopy = (text: string) => {
    onCopy(text);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        {prompt ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <span>Prompt Details</span>
                <div className="flex items-center">
                  {prompt.score && (
                    <Badge variant="secondary" className="ml-2">
                      <Star className="w-4 h-4 mr-1 text-yellow-500" />
                      Score: {prompt.score}
                    </Badge>
                  )}
                </div>
              </DialogTitle>
              <DialogDescription>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  <span>{formatDistanceToNow(new Date(prompt.created_at), { addSuffix: true })}</span>
                  <Tag className="w-4 h-4 ml-2" />
                  <Badge className={`${getCategoryColor(prompt.category)} text-white`}>
                    {prompt.category}
                  </Badge>
                </div>
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 mt-4">
              {/* Original Prompt */}
              <div>
                <h4 className="text-sm font-medium mb-2">Original Prompt</h4>
                <div className="relative">
                  <div className="p-4 rounded-lg bg-muted/50">
                    {prompt.raw_prompt}
                    <Button
                      size="icon"
                      variant="ghost"
                      className="absolute top-2 right-2 h-8 w-8"
                      onClick={() => handleCopy(prompt.raw_prompt)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Refined Prompt */}
              <div>
                <h4 className="text-sm font-medium mb-2">Refined Prompt</h4>
                <div className="relative">
                  <div className="p-4 rounded-lg bg-muted">
                    {prompt.refined_prompt}
                    <Button
                      size="icon"
                      variant="ghost"
                      className="absolute top-2 right-2 h-8 w-8"
                      onClick={() => handleCopy(prompt.refined_prompt)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Feedback */}
              {prompt.feedback && prompt.feedback.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 flex items-center">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Feedback
                  </h4>
                  <ul className="space-y-2">
                    {prompt.feedback.map((item, index) => (
                      <li key={index} className="text-sm text-muted-foreground">
                        • {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default PromptHistoryModal; 